<template>
  <v-container class="mb-12">
    <v-flex>
      <v-layout justify-center>
        <Logo></Logo>
      </v-layout>

      <h1 class="text-center mb-4">Output Description</h1>

      <div>
        <v-card class="mb-4" v-for="description in DESCRIPTIONS" :key="description.id">
          <v-card-title class="title">{{description.title}}</v-card-title>
          <v-card-text>
            <div v-html="description.body"></div>
            <v-flex hidden-sm-and-down mt-3 v-if="description.id === 'gene'">
              <span>Examples:</span>
              <v-simple-table>
                <thead>
                  <tr>
                    <th>Species</th>
                    <th>Gene symbol (displayed)</th>
                    <th>Active alias gene names</th>
                  </tr>
                  </thead>
                  <tbody>
                  <tr>
                    <td>Homo sapiens</td>
                    <td>MTOR</td>
                    <td>ENSG00000198793, 2475, mechanistic target of rapamycin kinase, FRAP,FRAP1,FRAP2,RAFT1,RAPT1,SKS</td>
                  </tr>
                  <tr>
                    <td>Mus musculus</td>
                    <td>Xrcc5</td>
                    <td>ENSMUSG00000026187, 22596, X-ray repair complementing defective repair in Chinese hamster cells 5, AI314015,CTC85,CTCBF,Ku80,Ku86</td>
                  </tr>
                  <tr>
                    <td>Drosophila melanogaster</td>
                    <td>Cyp4ad1</td>
                    <td>FBgn0033292, 35821, Cyp4ad1	4ad1,BEST:LD02646,CG2110,Dmel\CG2110</td>
                  </tr>
                  <tr>
                    <td>Caenorhabditis elegans</td>
                    <td>exos-4.1</td>
                    <td>WBGene00007201, 24105309, Putative exosome complex component RRP41</td>
                  </tr>
                  <tr>
                    <td>Rattus norvegicus</td>
                    <td>Ldlr</td>
                    <td>ENSRNOG00000009946, 300438, low density lipoprotein receptor, LDLRA</td>
                  </tr>
                </tbody>
              </v-simple-table>
            </v-flex>
          </v-card-text>
        </v-card>

        <v-card class="mb-4">
          <v-card-title class="title">Column 13 - Very likely off-targets OT>95 (only present in downloaded data tables)</v-card-title>
          <v-card-text>
            Gives the number of very likely expected Off-targets (OT) (with an Off-target score greater than 95 (range 0-100)) The OT score was described previously in Michlits et al. 2017.
          </v-card-text>
        </v-card>

        <v-card class="mb-4">
          <v-card-title class="title">Column 14 - Very likely off-target genes (only present in downloaded data tables)</v-card-title>
          <v-card-text>
            Lists details for very likely off-targets, displays the gene symbol and the off-target sequence.
          </v-card-text>
        </v-card>

        <v-card class="mb-4">
          <v-card-title class="title">Column 15 - Likely off-targets OT>90 (only present in downloaded data tables)</v-card-title>
          <v-card-text>
            Gives the number of very likely expected Off-targets (OT) (with an Off-target score greater than 90 (range 0-100)) The OT score was described previously in Michlits et al. 2017.
          </v-card-text>
        </v-card>

        <v-card class="mb-4">
          <v-card-title class="title">Column 16 - Likely off-target genes (only present in downloaded data tables)</v-card-title>
          <v-card-text>
            Lists details for likely off-targets, displays the gene symbol and the off-target sequence.
          </v-card-text>
        </v-card>

        <v-card class="mb-4">
          <v-card-title class="title">Column 17 - Perfect match sites in genome (only present in downloaded data tables)</v-card-title>
          <v-card-text>
            Gives the number of perfect match sites found the genome including intronic and intergenic regions that were not included in off-target predictions.<br>
            In some cases genes are duplicated in the genome so all sgRNAs targeting this gene would have 2 perfect match sites.
          </v-card-text>
        </v-card>

        <v-card class="mb-4">
          <v-card-title class="title">Column 18 - Recommended sgRNA-length (only present in downloaded data tables)</v-card-title>
          <v-card-text>
            sgRNA transcription from U6 pol-III promotor sequence requires a G at the start of the sgRNA this G can be a naturally occuring G in the sgRNA Sequence or an extra G added to the 5’ end of the sgRNA.<br>
            If sgRNAs of different length can be used we give our preferred sgRNA length. We prefer 19nt sgRNAs over 20nt sgRNAs over 18nt sgRNA over extraG+20nt sgRNAs.
          </v-card-text>
        </v-card>

        <v-card class="mb-4">
          <v-card-title class="title">Column 19 - GeCKO cloning FW_oligo (only present in downloaded data tables)</v-card-title>
          <v-card-text>
            FW oligo sequences to be used with the <a href="http://genome-engineering.org/gecko/wp-content/uploads/2013/12/lentiCRISPRv2-and-lentiGuide-oligo-cloning-protocol.pdf" target="_blank">GeCKO cloning protocol</a>.<br><br>
            Improved lentiviral vectors and genome-wide libraries for CRISPR screening. Sanjana NE*, Shalem O*,
            Zhang F. Nature Methods (2014)<br><br>
            Genome-scale CRISPR-Cas9 knockout screening in human cells. Shalem O*, Sanjana NE*, Hartenian E, Shi X, Scott DA, Mikkelsen T, Heckl D, Ebert BL, Root DE, Doench JG, Zhang F (2014). Science, 343, 83-7. DOI: 10.1126/science.1247005
          </v-card-text>
        </v-card>

        <v-card class="mb-4">
          <v-card-title class="title">Column 20 - GeCKO cloning RV_oligo (only present in downloaded data tables)</v-card-title>
          <v-card-text>
            RV oligo sequences to be used with the <a href="http://genome-engineering.org/gecko/wp-content/uploads/2013/12/lentiCRISPRv2-and-lentiGuide-oligo-cloning-protocol.pdf" target="_blank">GeCKO cloning protocol</a>.<br><br>
            Improved lentiviral vectors and genome-wide libraries for CRISPR screening. Sanjana NE*, Shalem O*,
            Zhang F. Nature Methods (2014)<br><br>
            Genome-scale CRISPR-Cas9 knockout screening in human cells. Shalem O*, Sanjana NE*, Hartenian E, Shi X, Scott DA, Mikkelsen T, Heckl D, Ebert BL, Root DE, Doench JG, Zhang F (2014). Science, 343, 83-7. DOI: 10.1126/science.1247005
          </v-card-text>
        </v-card>

        <v-card class="mb-4">
          <v-card-title class="title">Column 21 - Additional properties (only present in downloaded data tables)</v-card-title>
          <v-card-text>
            Lists warnings that may be relevant depending on the application. E.g. For cloning strategies using restriction enzymes BbSI or BsmBI it may be relevant that the sgRNA does not contain these restriction sites.<br>
            Also a warning for multiple T-streches is included which is relevant if using pol-III promotors to express sgRNAs.
          </v-card-text>
        </v-card>

        <v-card class="mb-4">
          <v-card-title class="title">Column 22 - Alias gene names</v-card-title>
          <v-card-text>
            Includes ensemble ID, entrez ID and other alias gene names which can be used to search genes of interest.
          </v-card-text>
        </v-card>
      </div>
    </v-flex>
  </v-container>
</template>

<script>
  import Logo from '../components/Logo';
  import { DESCRIPTIONS } from '../global';

  export default {
    name: 'Description',
    components: {
      Logo
    },
    data() {
      return {
        DESCRIPTIONS
      }
    }
  }
</script>
